import React from 'react';
import { Calculator, Clock, Plus, Minus, X, Divide, Hash, Type, ListOrdered } from 'lucide-react';

export type ComputedOperationType = 
  | 'dateDiffSeconds'
  | 'dateDiffMinutes'
  | 'dateDiffHours'
  | 'dateDiffDays'
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide'
  | 'percentage'
  | 'arraySum'
  | 'arrayCount'
  | 'arrayAvg'
  | 'concat'
  | 'constant';

export interface ComputedFieldConfig {
  type: 'computed';
  operation: ComputedOperationType;
  operand1: string;
  operand2?: string;
  arrayField?: string;
  constantValue?: number | string;
  separator?: string;
  roundTo?: number;
}

interface OperationCategory {
  label: string;
  icon: React.ReactNode;
  operations: Array<{
    value: ComputedOperationType;
    label: string;
    description: string;
    requiresSecondOperand: boolean;
    requiresArrayField?: boolean;
    isArrayOp?: boolean;
    isConstant?: boolean;
  }>;
}

const operationCategories: OperationCategory[] = [
  {
    label: 'Date/Time',
    icon: <Clock className="w-4 h-4" />,
    operations: [
      { value: 'dateDiffSeconds', label: 'Time Difference (seconds)', description: 'End date - Start date in seconds', requiresSecondOperand: true },
      { value: 'dateDiffMinutes', label: 'Time Difference (minutes)', description: 'End date - Start date in minutes', requiresSecondOperand: true },
      { value: 'dateDiffHours', label: 'Time Difference (hours)', description: 'End date - Start date in hours', requiresSecondOperand: true },
      { value: 'dateDiffDays', label: 'Time Difference (days)', description: 'End date - Start date in days', requiresSecondOperand: true },
    ]
  },
  {
    label: 'Math',
    icon: <Calculator className="w-4 h-4" />,
    operations: [
      { value: 'add', label: 'Add (+)', description: 'Value A + Value B', requiresSecondOperand: true },
      { value: 'subtract', label: 'Subtract (-)', description: 'Value A - Value B', requiresSecondOperand: true },
      { value: 'multiply', label: 'Multiply (x)', description: 'Value A x Value B', requiresSecondOperand: true },
      { value: 'divide', label: 'Divide (/)', description: 'Value A / Value B', requiresSecondOperand: true },
      { value: 'percentage', label: 'Percentage (%)', description: '(Value / Total) x 100', requiresSecondOperand: true },
    ]
  },
  {
    label: 'Array/List',
    icon: <ListOrdered className="w-4 h-4" />,
    operations: [
      { value: 'arraySum', label: 'Sum Array', description: 'Sum all values in array', requiresSecondOperand: false, requiresArrayField: true, isArrayOp: true },
      { value: 'arrayCount', label: 'Count Items', description: 'Count items in array', requiresSecondOperand: false, isArrayOp: true },
      { value: 'arrayAvg', label: 'Average', description: 'Average of array values', requiresSecondOperand: false, requiresArrayField: true, isArrayOp: true },
    ]
  },
  {
    label: 'Text',
    icon: <Type className="w-4 h-4" />,
    operations: [
      { value: 'concat', label: 'Concatenate', description: 'Join two text values', requiresSecondOperand: true },
    ]
  },
  {
    label: 'Constant',
    icon: <Hash className="w-4 h-4" />,
    operations: [
      { value: 'constant', label: 'Fixed Value', description: 'Use a constant value', requiresSecondOperand: false, isConstant: true },
    ]
  }
];

interface ComputedFieldBuilderProps {
  config: ComputedFieldConfig;
  availablePaths: string[];
  detectedArrays?: Array<{ path: string; sampleItemKeys: string[] }>;
  onChange: (config: ComputedFieldConfig) => void;
  onCancel: () => void;
}

function ComputedFieldBuilder({
  config,
  availablePaths,
  detectedArrays = [],
  onChange,
  onCancel
}: ComputedFieldBuilderProps) {
  const findOperation = (opValue: ComputedOperationType) => {
    for (const cat of operationCategories) {
      const op = cat.operations.find(o => o.value === opValue);
      if (op) return op;
    }
    return null;
  };

  const currentOperation = findOperation(config.operation);

  const handleOperationChange = (operation: ComputedOperationType) => {
    const opDetails = findOperation(operation);
    onChange({
      ...config,
      operation,
      operand2: opDetails?.requiresSecondOperand ? config.operand2 || '' : undefined,
      arrayField: opDetails?.requiresArrayField ? config.arrayField || '' : undefined,
      constantValue: opDetails?.isConstant ? config.constantValue || 0 : undefined,
    });
  };

  const handleOperand1Change = (value: string) => {
    onChange({ ...config, operand1: value });
  };

  const handleOperand2Change = (value: string) => {
    onChange({ ...config, operand2: value });
  };

  const handleArrayFieldChange = (value: string) => {
    onChange({ ...config, arrayField: value });
  };

  const handleConstantChange = (value: string) => {
    const numVal = parseFloat(value);
    onChange({ ...config, constantValue: isNaN(numVal) ? value : numVal });
  };

  const handleRoundToChange = (value: string) => {
    const num = parseInt(value, 10);
    onChange({ ...config, roundTo: isNaN(num) ? undefined : num });
  };

  const handleSeparatorChange = (value: string) => {
    onChange({ ...config, separator: value });
  };

  const getArrayItemFields = (): string[] => {
    const arrayInfo = detectedArrays.find(a => a.path === config.operand1);
    return arrayInfo?.sampleItemKeys || [];
  };

  const getOperatorIcon = () => {
    if (!currentOperation) return null;
    
    switch (config.operation) {
      case 'add': return <Plus className="w-5 h-5 text-success" />;
      case 'subtract': return <Minus className="w-5 h-5 text-destructive" />;
      case 'multiply': return <X className="w-5 h-5 text-info" />;
      case 'divide': return <Divide className="w-5 h-5 text-primary" />;
      default: return <Calculator className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const renderFormulaPreview = () => {
    const op1 = config.operand1 || '?';
    const op2 = config.operand2 || '?';
    
    switch (config.operation) {
      case 'dateDiffSeconds':
      case 'dateDiffMinutes':
      case 'dateDiffHours':
      case 'dateDiffDays': {
        const unit = config.operation.replace('dateDiff', '').toLowerCase();
        return `(${op1} - ${op2}) in ${unit}`;
      }
      case 'add':
        return `${op1} + ${op2}`;
      case 'subtract':
        return `${op1} - ${op2}`;
      case 'multiply':
        return `${op1} x ${op2}`;
      case 'divide':
        return `${op1} / ${op2}`;
      case 'percentage':
        return `(${op1} / ${op2}) x 100`;
      case 'arraySum':
        return config.arrayField ? `SUM(${op1}[].${config.arrayField})` : `SUM(${op1}[])`;
      case 'arrayCount':
        return `COUNT(${op1}[])`;
      case 'arrayAvg':
        return config.arrayField ? `AVG(${op1}[].${config.arrayField})` : `AVG(${op1}[])`;
      case 'concat':
        return `${op1} + "${config.separator ?? ' '}" + ${op2}`;
      case 'constant':
        return `${config.constantValue ?? '0'}`;
      default:
        return '...';
    }
  };

  return (
    <div className="bg-accent border border-primary rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calculator className="w-5 h-5 text-primary" />
          <span className="font-semibold text-primary">
            Computed Field
          </span>
        </div>
        <button
          onClick={onCancel}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Switch to direct field
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Operation Type
        </label>
        <select
          value={config.operation}
          onChange={(e) => handleOperationChange(e.target.value as ComputedOperationType)}
          className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {operationCategories.map(cat => (
            <optgroup key={cat.label} label={cat.label}>
              {cat.operations.map(op => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {currentOperation && (
          <p className="mt-1 text-xs text-muted-foreground">
            {currentOperation.description}
          </p>
        )}
      </div>

      {currentOperation?.isConstant && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Constant Value
          </label>
          <input
            type="text"
            value={config.constantValue ?? ''}
            onChange={(e) => handleConstantChange(e.target.value)}
            placeholder="Enter a constant value"
            className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}

      {!currentOperation?.isConstant && (
        <>
          <div className="flex items-center space-x-3 py-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                {currentOperation?.isArrayOp ? 'Array Path' : 
                 config.operation.startsWith('dateDiff') ? 'End Date' : 'Value A'}
              </label>
              <select
                value={config.operand1 || ''}
                onChange={(e) => handleOperand1Change(e.target.value)}
                className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              >
                <option value="">Select field...</option>
                {currentOperation?.isArrayOp ? (
                  detectedArrays.map(arr => (
                    <option key={arr.path} value={arr.path}>
                      {arr.path} (array)
                    </option>
                  ))
                ) : (
                  availablePaths.map(path => (
                    <option key={path} value={path}>{path}</option>
                  ))
                )}
              </select>
            </div>

            {currentOperation?.requiresSecondOperand && (
              <>
                <div className="flex items-center justify-center w-10 h-10 bg-card rounded-full shadow-sm border border-border mt-5">
                  {getOperatorIcon()}
                </div>

                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    {config.operation.startsWith('dateDiff') ? 'Start Date' : 
                     config.operation === 'percentage' ? 'Total' : 'Value B'}
                  </label>
                  <select
                    value={config.operand2 || ''}
                    onChange={(e) => handleOperand2Change(e.target.value)}
                    className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  >
                    <option value="">Select field...</option>
                    {availablePaths.map(path => (
                      <option key={path} value={path}>{path}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {currentOperation?.requiresArrayField && config.operand1 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Field to Aggregate
              </label>
              <select
                value={config.arrayField || ''}
                onChange={(e) => handleArrayFieldChange(e.target.value)}
                className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select field within array items...</option>
                {getArrayItemFields().map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Select which field from each item in the array to use for the calculation
              </p>
            </div>
          )}

          {config.operation === 'concat' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Separator
              </label>
              <input
                type="text"
                value={config.separator ?? ' '}
                onChange={(e) => handleSeparatorChange(e.target.value)}
                placeholder="Space, comma, etc."
                className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
        </>
      )}

      {['dateDiffSeconds', 'dateDiffMinutes', 'dateDiffHours', 'dateDiffDays', 
        'add', 'subtract', 'multiply', 'divide', 'percentage', 'arraySum', 'arrayAvg'].includes(config.operation) && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Round to Decimal Places (optional)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={config.roundTo ?? ''}
            onChange={(e) => handleRoundToChange(e.target.value)}
            placeholder="e.g., 2 for two decimal places"
            className="w-full border border-input p-2 rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}

      <div className="bg-card rounded-lg p-3 border border-border">
        <div className="text-xs font-medium text-muted-foreground mb-1">
          Formula Preview
        </div>
        <code className="text-sm text-primary font-mono">
          {renderFormulaPreview()}
        </code>
      </div>
    </div>
  );
}

export default ComputedFieldBuilder;

